import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import { JOURNAL_ENTRIES, findEntry } from "@/data/journal";
import SeoHead from "@/components/site/SeoHead";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { month: "long", day: "numeric", year: "numeric" });

const JournalArticle = () => {
  const { slug } = useParams();
  const entry = findEntry(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!entry) return <Navigate to="/journal" replace />;

  const others = JOURNAL_ENTRIES.filter((e) => e.slug !== slug).slice(0, 3);

  return (
    <article className="pt-24 md:pt-32 pb-24" data-testid={`journal-article-${slug}`}>
      <SeoHead
        title={entry.title}
        description={entry.dek || entry.kicker || "An essay from the MELAVI Journal."}
        path={`/journal/${slug}`}
        image={entry.hero || entry.cover}
        type="article"
      />
      {/* Hero */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <Link
          to="/journal"
          className="nav-item text-ink/60 hover:text-ink"
          data-testid="article-back"
        >
          ← All essays
        </Link>
      </div>

      <header className="max-w-[900px] mx-auto px-6 md:px-12 pt-8 md:pt-12 text-center">
        <div className="nav-item mb-4" style={{ color: "var(--accent)" }}>
          {entry.kicker}
        </div>
        <h1
          className="serif text-4xl md:text-6xl leading-[1.05]"
          data-testid="article-title"
        >
          {entry.title}
        </h1>
        <p className="mt-6 serif italic text-lg md:text-2xl leading-relaxed text-ink/80 max-w-[54ch] mx-auto">
          {entry.dek}
        </p>
        <div className="mt-8 nav-item text-ink/60">
          By {entry.author} · {formatDate(entry.date)} · {entry.read} read
        </div>
      </header>

      {/* Hero image */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-12 md:mt-16">
        <div className="overflow-hidden">
          <img
            src={imgUrl(entry.heroImage || entry.image)}
            alt={entry.title}
            className="w-full aspect-[16/9] md:aspect-[21/9] object-cover"
          />
        </div>
      </div>

      {/* Body */}
      <div
        className="max-w-[720px] mx-auto px-6 md:px-0 mt-14 md:mt-20"
        data-testid="article-body"
      >
        {entry.body.map((node, i) => {
          if (node.type === "quote") {
            return (
              <blockquote
                key={i}
                className="my-14 md:my-20 hairline-t hairline-b py-10 md:py-14"
                data-testid={`article-quote-${i}`}
              >
                <div
                  className="serif italic text-2xl md:text-3xl leading-[1.5] text-center"
                  style={{ color: "var(--ink)" }}
                >
                  &ldquo;{node.text}&rdquo;
                </div>
              </blockquote>
            );
          }
          // First paragraph gets a drop-cap
          const isFirst = i === 0;
          return (
            <p
              key={i}
              className={`text-[17px] md:text-[19px] leading-[1.75] text-ink/85 mb-6 md:mb-8 ${
                isFirst ? "first-letter:font-serif first-letter:float-left first-letter:mr-3 first-letter:text-[64px] first-letter:leading-[0.9] first-letter:font-normal" : ""
              }`}
              style={undefined}
              data-testid={`article-p-${i}`}
            >
              {node.text}
            </p>
          );
        })}

        {/* Signoff */}
        <div className="mt-16 hairline-t pt-10 flex flex-col items-center gap-5 text-center" data-testid="article-signoff">
          <img
            src={imgUrl("/api/uploads/team/founders-sm.webp")}
            alt="Meghana TR and Lallitha Mukund"
            className="w-28 h-28 md:w-32 md:h-32 object-cover rounded-full"
            style={{ border: "1px solid var(--stone)" }}
            data-testid="article-signoff-avatar"
          />
          <div>
            <div className="serif italic text-xl" style={{ color: "var(--accent)" }}>
              — {entry.author}
            </div>
            <div className="mt-2 nav-item text-ink/50">MELAVI · {formatDate(entry.date)}</div>
            <div className="mt-4 text-sm text-ink/70 max-w-[42ch] mx-auto leading-relaxed">
              Meghana &amp; Lallitha founded MELAVI in Bengaluru in 2024 —
              small clothes, made slowly, for children and the grown-ups they love.
            </div>
          </div>
        </div>
      </div>

      {/* More essays */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 mt-24 md:mt-32">
        <div className="hairline-t pt-14">
          <div className="nav-item mb-8" style={{ color: "var(--stone)" }}>
            Keep reading
          </div>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {others.map((e) => (
              <Link
                key={e.slug}
                to={`/journal/${e.slug}`}
                className="group block"
                data-testid={`article-more-${e.slug}`}
              >
                <div className="overflow-hidden bg-sand">
                  <img
                    src={imgUrl(e.image)}
                    alt={e.title}
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                  />
                </div>
                <div className="mt-4 nav-item text-ink/60">{e.kicker}</div>
                <h4 className="serif text-xl md:text-2xl leading-tight mt-1">{e.title}</h4>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
};

export default JournalArticle;
